import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { Container } from "@/components/shared";
import { Award, MapPin, MessageCircle, Heart, ArrowLeft, Loader2, UserCog } from "lucide-react";

const CAT_LABEL = {
  restaurant: "Restaurant", librairie: "Librairie", association: "Association", coiffure: "Coiffure",
  evenement: "Événement", festival: "Festival", exposition: "Exposition", musee: "Musée",
  coworking_business: "Business", entrepreneur: "Entrepreneur", artiste: "Artiste",
  tourisme: "Tourisme", association_etudiante: "Asso étudiante", commerce: "Commerce", autre: "Autre",
};

export function Avatar({ src, name, size = 40 }) {
  const url = mediaUrl(src);
  if (url) return <img src={url} alt={name} data-testid="member-avatar-img" className="rounded-full object-cover shrink-0 border border-gold/25" style={{ width: size, height: size }} />;
  return (
    <div className="rounded-full gold-gradient flex items-center justify-center text-black font-display shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.42 }}>
      {(name?.[0] || "M").toUpperCase()}
    </div>
  );
}

export default function MemberProfile() {
  const { userId } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/profiles/${userId}`).then(({ data }) => setP(data)).catch(() => setP(false)).finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <Container><div className="flex justify-center py-20"><Loader2 className="animate-spin text-gold" /></div></Container>;
  if (!p) return <Container><p className="text-white/40 py-16 text-center">Membre introuvable.</p></Container>;

  const since = p.member_since ? new Date(p.member_since).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : "";
  const stats = [
    { label: "Lieux ajoutés", value: p.stats.places, icon: MapPin },
    { label: "Avis publiés", value: p.stats.reviews, icon: Award },
    { label: "Discussions", value: p.stats.posts, icon: MessageCircle },
  ];

  return (
    <Container>
      <button onClick={() => window.history.back()} data-testid="member-back"
        className="inline-flex items-center gap-2 text-white/50 hover:text-gold text-sm mb-6"><ArrowLeft size={15} /> Retour</button>

      <div className="card-surface p-6 mb-6" data-testid="member-profile">
        <div className="flex items-start gap-4">
          <Avatar src={p.avatar} name={p.name} size={72} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl tracking-tight" data-testid="member-name">{p.name}</h1>
              <span className="text-[11px] text-gold border border-gold/30 rounded-full px-2 py-0.5">Niveau {p.level}</span>
            </div>
            {since && <p className="text-white/40 text-xs mt-1">Membre depuis {since}</p>}
            {p.bio && <p className="text-white/70 text-sm mt-3 leading-relaxed" data-testid="member-bio">{p.bio}</p>}
            {p.is_me && (
              <Link to="/profile" data-testid="member-edit-link"
                className="inline-flex items-center gap-1.5 mt-3 text-xs text-gold/80 hover:text-gold border border-gold/25 rounded-full px-3 py-1"><UserCog size={13} /> Modifier mon profil</Link>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        {stats.map((s) => (
          <div key={s.label} className="card-surface p-4 text-center" data-testid={`member-stat-${s.label}`}>
            <s.icon size={18} className="text-gold mx-auto mb-1.5" strokeWidth={1.5} />
            <div className="font-display text-2xl">{s.value}</div>
            <div className="text-white/40 text-[11px] mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {p.badges.length > 0 && (
        <div className="mb-8">
          <h2 className="font-display text-lg mb-3">Badges</h2>
          <div className="flex flex-wrap gap-2" data-testid="member-badges">
            {p.badges.map((b, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 bg-gold/[0.06] text-gold px-3 py-1.5 text-sm">
                <Award size={13} /> {b}
              </span>
            ))}
          </div>
        </div>
      )}

      {p.recent_places.length > 0 && (
        <div className="mb-8">
          <h2 className="font-display text-lg mb-3">Lieux partagés sur la Carte Afro</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {p.recent_places.map((pl) => (
              <Link key={pl.place_id} to={`/carte-afro?place=${pl.place_id}`} data-testid={`member-place-${pl.place_id}`}
                className="card-surface p-4 flex items-center gap-3 hover:border-gold/30 transition-colors">
                {pl.photo ? <img src={mediaUrl(pl.photo)} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                  : <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center shrink-0"><MapPin size={18} className="text-gold/60" /></div>}
                <div className="min-w-0">
                  <p className="font-medium truncate">{pl.name}</p>
                  <p className="text-white/40 text-xs">{CAT_LABEL[pl.category] || pl.category}{pl.city ? ` · ${pl.city}` : ""}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {p.recent_posts.length > 0 && (
        <div className="mb-8">
          <h2 className="font-display text-lg mb-3">Discussions récentes</h2>
          <div className="space-y-2">
            {p.recent_posts.map((po) => (
              <Link key={po.post_id} to={`/espace-communautaire?post=${po.post_id}`} data-testid={`member-post-${po.post_id}`}
                className="block card-surface p-4 hover:border-gold/30 transition-colors">
                <p className="font-medium">{po.title}</p>
                <div className="flex items-center gap-4 mt-1.5 text-white/40 text-xs">
                  <span className="flex items-center gap-1"><Heart size={12} /> {po.like_count}</span>
                  <span className="flex items-center gap-1"><MessageCircle size={12} /> {po.comment_count}</span>
                  {po.city && <span>· {po.city}</span>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {p.recent_places.length === 0 && p.recent_posts.length === 0 && (
        <p className="text-white/30 text-sm text-center py-8">Ce membre n'a pas encore d'activité publique.</p>
      )}
    </Container>
  );
}
